/**
 * Metadata names as chips on Lane V2 (UX map C16 / 5.7): the names this session's tasks actually touch become clickable in prose,
 * tables, and the outcome / plan rows. Pure: built from the R3 snapshots the store already holds, nothing fetched.
 *
 * A name in THIS task's package opens that component's diff in the panel; any other name known to the session (a plan item, a build
 * item with no package row yet) opens the metadata explorer (`/studio/metadata?node=Type:Name`, `pages/migrations/Explorer.tsx`).
 * Only names the session touched are chips, never "every capitalised word" (`ProseEntityLinker`: a link that dead-ends is worse than text).
 */
import { METADATA_TYPE_TO_ENTITY_TYPE } from "@/pages/migrations/explorerNodes";
import { itemKey } from "@/components/organisms/multi-agent/laneV2/views";
import type { TaskSnapshot } from "./types";

export interface ChipTarget {
  metadataType: string;
  /** The name as the session knows it (`Account.Region__c` for a field). */
  fullName: string;
  /** Set when the component is a member of a task's package that has a viewable diff. */
  taskId: string | null;
  workItemId: string | null;
  /** The component exists in the org to look at: false for a component this work creates (an explorer link would dead-end). */
  explorable: boolean;
}

export type ChipAction = { kind: "diff"; taskId: string; workItemId: string } | { kind: "explorer"; href: string };

/** Below this a token is too generic to link (matches `useEntityLinks`). */
export const MIN_CHIP_LENGTH = 3;

/**
 * The explorer deep link for a component, or null when the explorer cannot show it: it only knows ApexClass, ApexTrigger,
 * CustomObject and Flow (`explorerNodes`), so a field links to its parent object and any other type has no link.
 */
export function explorerHref(metadataType: string, fullName: string): string | null {
  let type = metadataType;
  let name = fullName;
  if (metadataType === "CustomField" && fullName.includes(".")) {
    type = "CustomObject";
    name = fullName.slice(0, fullName.indexOf("."));
  }
  if (!(type in METADATA_TYPE_TO_ENTITY_TYPE)) return null;
  return `/studio/metadata?node=${encodeURIComponent(`${type}:${name}`)}`;
}

function isIdentChar(ch: string): boolean {
  return (ch >= "A" && ch <= "Z") || (ch >= "a" && ch <= "z") || (ch >= "0" && ch <= "9") || ch === "_";
}

/**
 * The single identifier prose can carry for a component: the whole name, or the part after the last dot for a member
 * (`Account.Region__c` → `Region__c`). A name with any other character (spaces, dashes) has no token and is not chipped in prose.
 */
export function chipToken(fullName: string): string | null {
  const leaf = fullName.slice(fullName.lastIndexOf(".") + 1);
  if (leaf.length < MIN_CHIP_LENGTH) return null;
  for (const ch of fullName.split(".").join("")) if (!isIdentChar(ch)) return null;
  return leaf;
}

/** The click of one chip: the diff when the component is in a package with a body, else the explorer; null for a brand-new component with no diff (plain text). */
export function chipActionOf(target: ChipTarget): ChipAction | null {
  if (target.taskId && target.workItemId) return { kind: "diff", taskId: target.taskId, workItemId: target.workItemId };
  if (!target.explorable) return null;
  const href = explorerHref(target.metadataType, target.fullName);
  return href ? { kind: "explorer", href } : null;
}

/** The chip of one component named by a row (outcome / plan), looked up by its type and full name. */
export function chipTargetFor(
  targets: ReadonlyMap<string, ChipTarget>,
  metadataType: string,
  fullName: string,
  explorable = true,
): ChipTarget {
  return targets.get(itemKey(metadataType, fullName)) ?? { metadataType, fullName, taskId: null, workItemId: null, explorable };
}

export interface ChipIndex {
  /** token (what prose carries) → target. First writer wins; package members are written first; a token two different components share is left out. */
  byToken: Map<string, ChipTarget>;
  /** `itemKey(Type, FullName)` (case-folded, like the member / work-item join) → target, for rows that name the component exactly. */
  byKey: Map<string, ChipTarget>;
}

/** Package members first (with their diff), then plan items, then build items: the most openable meaning of a name wins. */
export function buildChipIndex(snapshots: Readonly<Record<string, TaskSnapshot | null | undefined>>): ChipIndex {
  const byToken = new Map<string, ChipTarget>();
  const byKey = new Map<string, ChipTarget>();
  // A short name two components share (Account.Status__c / Case.Status__c) cannot be linked from prose: it would open the wrong one.
  const ambiguous = new Set<string>();
  const add = (t: ChipTarget) => {
    const key = itemKey(t.metadataType, t.fullName);
    const existing = byKey.get(key);
    if (existing) {
      // A later source may only add the diff the earlier one lacked.
      if (!existing.workItemId && t.workItemId) {
        existing.taskId = t.taskId;
        existing.workItemId = t.workItemId;
      }
      return;
    }
    byKey.set(key, t);
    const token = chipToken(t.fullName);
    if (token) {
      if (!byToken.has(token)) byToken.set(token, t);
      else ambiguous.add(token);
    }
  };
  const snaps = Object.values(snapshots).filter((s): s is TaskSnapshot => !!s);
  for (const s of snaps) {
    const items = new Map((s.build?.items ?? []).map((i) => [itemKey(i.metadata_type, i.full_name), i] as const));
    for (const m of s.package?.members ?? []) {
      const item = items.get(itemKey(m.metadata_type, m.full_name));
      add({ metadataType: m.metadata_type, fullName: m.full_name, taskId: item?.has_body ? s.task_id : null, workItemId: item?.has_body ? item.work_item_id : null, explorable: m.op !== "create" });
    }
  }
  for (const s of snaps) for (const p of s.plan?.items ?? []) add({ metadataType: p.metadata_type, fullName: p.full_name, taskId: null, workItemId: null, explorable: p.op !== "create" });
  for (const s of snaps) {
    for (const i of s.build?.items ?? []) {
      add({ metadataType: i.metadata_type, fullName: i.full_name, taskId: i.has_body ? s.task_id : null, workItemId: i.has_body ? i.work_item_id : null, explorable: i.op !== "create" });
    }
  }
  for (const token of ambiguous) byToken.delete(token);
  return { byToken, byKey };
}

/** A string that is equal exactly when two indexes would produce the same chips and the same clicks. */
export function chipIndexSignature(index: ChipIndex): string {
  return Array.from(index.byKey, ([key, t]) => `${key}|${t.taskId ?? ""}|${t.workItemId ?? ""}|${t.explorable ? 1 : 0}`).join("\u0001");
}
